import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useAdSurfaceAndSlot } from '../../hooks/useAdSurfaceAndSlot';
import { pickStaticAd } from '../../data/staticAds';
import { selectAdsStaticEnabled } from '../../store/reducers/sitePublicReducer';
import { isValidAdDestination } from '../../utils/adDestinationUrl';
import { AdCardContainer, AdMedia, AdLabel, AdMeta, AdTitle, AdSponsor, AdCta } from './AdImageSlot.styled';

/**
 * Image-based slot. Uses `ad` when provided (e.g. managed merge); otherwise static `pickStaticAd`.
 */
export default function AdImageSlot({ size = 'medium', position = 'sidebar', slot, children, ad: adProp, pageKey, onLinkClick }) {
  const { loaded, slotVisible } = useAdSurfaceAndSlot(pageKey);
  const adsStaticEnabled = useSelector(selectAdsStaticEnabled);
  const index = typeof slot === 'number' ? slot : 0;
  const staticAd = useMemo(() => {
    if (!adsStaticEnabled) return null;
    return pickStaticAd({ position, size, slot: index });
  }, [adsStaticEnabled, position, size, index]);
  const ad = adProp != null ? adProp : staticAd;

  const handleClick = (e) => {
    const dest = isValidAdDestination(ad?.url);
    if (!dest) e.preventDefault();
    else onLinkClick?.(e);
  };

  if (!loaded || !slotVisible || !ad || !ad.image) return null;

  const dest = isValidAdDestination(ad.url);
  const isSidebar = position === 'sidebar';

  return (
    <AdCardContainer
      href={dest ? ad.url : '#'}
      onClick={handleClick}
      target={dest ? '_blank' : undefined}
      rel={dest ? 'noopener noreferrer sponsored' : undefined}
      $size={size}
      $position={position}
    >
      <AdLabel>Advertisement</AdLabel>
      {children || (
        <>
          <AdMedia $position={position}>
            <img src={ad.image} alt={ad.title} loading="lazy" decoding="async" />
          </AdMedia>
          {isSidebar && (
            <AdMeta>
              <AdTitle>{ad.title}</AdTitle>
              <AdSponsor>{ad.sponsor}</AdSponsor>
              {ad.cta && <AdCta>{ad.cta}</AdCta>}
            </AdMeta>
          )}
        </>
      )}
    </AdCardContainer>
  );
}
